import { useCallback, useMemo } from 'react'

import { GroupingDefinition } from '@factorialco/f0-react'
import { getYear, isToday, isWithinInterval } from 'date-fns'
import { startOfToday, subDays } from 'date-fns'
import {
  InboxTaskListItemFragment,
  NewInboxTasksQuery,
  TasksValueObjectsTaskTaskTimePeriodEnum,
} from 'generated/resources/graphql'

import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { DueDateState, dueDateState } from 'modules/inbox_tasks/lib/dueDate'

const CREATION_DATE_GROUP_DEFAULT_ORDER = ['today', 'last_week', 'last_month'] as const

export type CreationDateGroup = (typeof CREATION_DATE_GROUP_DEFAULT_ORDER)[number] | `${number}`

const DUE_DATE_GROUP_DEFAULT_ORDER = [
  'due_today',
  'due_soon',
  'due_upcoming',
  'due_tomorrow',
  'overdue',
  'no_due',
] as const

type DueDateGroup = (typeof DUE_DATE_GROUP_DEFAULT_ORDER)[number]

const getGroupTranslation = (groupId: CreationDateGroup | DueDateGroup): string => {
  const translations: Record<CreationDateGroup | DueDateGroup, string> = {
    today: i18n.t('common.date.groups.today'),
    last_week: i18n.t('common.date.groups.last_week'),
    last_month: i18n.t('common.date.groups.last_month'),
    due_today: i18n.t('dashboard.todo.due_date.today'),
    due_tomorrow: i18n.t('dashboard.todo.due_date.tomorrow'),
    overdue: i18n.t('dashboard.todo.due_date.overdue'),
    no_due: i18n.t('tasks.analytics.no_due'),
    due_soon: i18n.t('complaints.complaint.due_soon'),
    due_upcoming: i18n.t('finance.cost_centers.assignments.status.upcoming'),
  }

  return translations[groupId] ?? `${groupId}`
}

type Params = {
  data?: NonNullable<NewInboxTasksQuery['tasks']['timeCounts']>
}

export const useGrouping = ({ data }: Params) => {
  const OFFSETS = useMemo(
    () => ({
      lastMonth: i18nDateToString(subDays(startOfToday(), 30), 'backend_datetime'),
      lastWeek: i18nDateToString(subDays(startOfToday(), 7), 'backend_datetime'),
      today: i18nDateToString(startOfToday(), 'backend_datetime'),
    }),
    []
  )

  const getTimeCount = useCallback(
    (timePeriod: TasksValueObjectsTaskTaskTimePeriodEnum) =>
      data?.find((node) => node.timePeriod === timePeriod)?.count ?? undefined,
    [data]
  )

  const getCreationDateGroup = useCallback((item: InboxTaskListItemFragment): CreationDateGroup => {
    const createdAt = stringToDate(item.createdAt, 'backend_datetime').toDate()

    if (
      isWithinInterval(createdAt, {
        start: OFFSETS.lastMonth,
        end: OFFSETS.lastWeek,
      })
    ) {
      return 'last_month'
    }

    if (
      isWithinInterval(createdAt, {
        start: OFFSETS.lastWeek,
        end: OFFSETS.today,
      })
    ) {
      return 'last_week'
    }

    if (isToday(createdAt)) {
      return 'today'
    }

    return `${getYear(createdAt)}`
  }, [])

  const getDueDateGroup = useCallback((item: InboxTaskListItemFragment) => {
    const dueDate = item.dueOn ? stringToDate(item.dueOn, 'backend') : undefined

    const state = dueDate ? dueDateState(dueDate) : undefined

    let group: DueDateGroup = 'no_due'

    if (!state) {
      return group
    }

    const dueGroup: Record<DueDateState, DueDateGroup> = {
      'due-today': 'due_today',
      'due-tomorrow': 'due_tomorrow',
      overdue: 'overdue',
      upcoming: 'due_upcoming',
      'due-soon': 'due_soon',
    }

    group = dueGroup[state]

    return group
  }, [])

  const mapData = useCallback(
    (tasksData: NewInboxTasksQuery) => {
      const nodes = tasksData.tasks.inboxTasksConnection.edges?.map((edge) => edge.node)
      return nodes.map((node) => {
        const creationDateGroup = getCreationDateGroup(node)
        const dueDateGroup = getDueDateGroup(node)

        return {
          ...node,
          creationDateGroup,
          dueDateGroup,
        }
      })
    },
    [getCreationDateGroup, getDueDateGroup]
  )

  const grouping = useMemo<
    GroupingDefinition<
      InboxTaskListItemFragment & {
        dueDateGroup: DueDateGroup
        creationDateGroup: CreationDateGroup
      }
    >
  >(
    () => ({
      mandatory: true,
      collapsible: false,
      groupBy: {
        creationDateGroup: {
          name: i18n.t('common.creation_date'),
          label: (groupId: CreationDateGroup) => getGroupTranslation(groupId),
          defaultDirection: 'desc' as const,
          itemCount: (groupId: CreationDateGroup) => {
            switch (groupId) {
              case 'today':
              case 'last_week':
              case 'last_month':
                return getTimeCount(groupId)
              default:
                return
            }
          },
        },
        dueDateGroup: {
          name: i18n.t('common.due_date'),
          label: (groupId: DueDateGroup) => getGroupTranslation(groupId),
          defaultDirection: 'asc' as const,
          itemCount: (groupId: DueDateGroup) => {
            switch (groupId) {
              case 'due_today':
              case 'due_tomorrow':
              case 'due_soon':
              case 'due_upcoming':
              case 'overdue':
                return getTimeCount(groupId)
              default:
                return
            }
          },
        },
      },
    }),
    [getTimeCount]
  )

  return {
    mapData,
    grouping,
  }
}
